import React from 'react';
import {
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useDraftReply } from './useDraftReply';

type DraftReply = ReturnType<typeof useDraftReply>;

interface IDraftReplyPanelProps {
  reply: DraftReply;
}

/**
 * The member tab's floating draft panel. The inbox pane uses ReplyComposer
 * instead; both read the same useDraftReply state.
 */
export const DraftReplyPanel: React.FC<IDraftReplyPanelProps> = ({ reply }) => {
  const { draft, setDraft, drafting, draftError, copied, copyError } = reply;

  return (
    <Stack
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        width: 420,
        maxWidth: 'calc(100vw - 48px)',
        zIndex: 1200,
        backgroundColor: '#FFFFFF',
        border: '1px solid rgba(53, 103, 102, 0.4)',
        borderRadius: '12px',
        boxShadow: '0px 8px 24px rgba(33, 59, 84, 0.16)',
        padding: '16px',
        gap: '12px',
      }}
    >
      <Stack
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography
          sx={{ fontWeight: 700, fontSize: '15px', color: '#213B54' }}
        >
          Suggested reply
        </Typography>
        <Button
          size="small"
          onClick={reply.reset}
          sx={{ minWidth: 0, color: '#7D818C' }}
        >
          ✕
        </Button>
      </Stack>
      {drafting ? (
        <Stack alignItems="center" paddingY="24px">
          <CircularProgress size={28} sx={{ color: '#356766' }} />
        </Stack>
      ) : (
        <>
          {draftError ? (
            <Typography sx={{ color: '#B3261E', fontSize: '14px' }}>
              {draftError}
            </Typography>
          ) : (
            <>
              <TextField
                multiline
                minRows={4}
                maxRows={10}
                fullWidth
                value={draft || ''}
                onChange={(e) => setDraft(e.target.value)}
              />
              {!!copyError && (
                <Typography
                  sx={{ color: '#B3261E', fontSize: '14px', marginTop: '8px' }}
                >
                  {copyError}
                </Typography>
              )}
            </>
          )}
          <Stack flexDirection="row" justifyContent="flex-end" gap="8px">
            <Button
              onClick={reply.requestDraft}
              sx={{ color: '#356766', textTransform: 'none' }}
            >
              Regenerate
            </Button>
            {!draftError && (
              <Button
                variant="contained"
                onClick={reply.copyDraft}
                sx={{
                  backgroundColor: '#356766',
                  textTransform: 'none',
                  fontWeight: 600,
                  ':hover': { backgroundColor: '#213B54' },
                }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            )}
          </Stack>
        </>
      )}
    </Stack>
  );
};
